import React, { useState, useEffect } from 'react';
import { validateDigitalCardForm } from '../../utils/validation';
import DigitalIDCard from './DigitalIDCard';
import { getDigitalCardById } from '../../utils/dataManager';
import './DigitalIDForm.css';

const DigitalIDForm = ({ onSave, onCancel, editingCard = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    position: '',
    company_name: 'Jethro.inc',
    description: '',
    contact_info: {
      phone: '',
      whatsapp: '',
      email: ''
    },
    address: '',
    social_media: {
      linkedin: '',
      instagram: ''
    },
    profile_image: '',
    visibility: {
      show_phone: true,
      show_whatsapp: true
    }
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    const loadEditingCard = async () => {
      if (editingCard) {
        // Load full card data from database to get profile_image
        let fullCardData = editingCard;
        if (editingCard.id) {
          try {
            const dbCard = await getDigitalCardById(editingCard.id);
            if (dbCard) {
              fullCardData = dbCard;
            }
          } catch (error) {
            console.error('❌ Error loading full card data:', error);
          }
        }
        
        setFormData({
          ...fullCardData,
          contact_info: fullCardData.contact_info || {
            phone: '',
            whatsapp: '',
            email: ''
          },
          social_media: fullCardData.social_media || {
            linkedin: '',
            instagram: ''
          },
          // Use profile_image from database (base64) or empty string
          profile_image: fullCardData.profile_image || '',
          company_name: fullCardData.company_name || 'Jethro.inc',
          visibility: fullCardData.visibility || {
            show_phone: true,
            show_whatsapp: true
          }
        });
      }
    };
    
    loadEditingCard();
  }, [editingCard]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleImageUpload = (e, imageType) => {
    const file = e.target.files[0];
      if (file) {
      // Allow larger profile images up to 5MB
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({
          ...prev,
          [imageType]: 'Image size must be less than 5MB'
        }));
        return;
      }
      if (!file.type.startsWith('image/')) {
        setErrors(prev => ({
          ...prev,
          [imageType]: 'Please select a valid image file'
        }));
        return;
      }
      const uploadImageLocally = async (file) => {
        setErrors(prev => ({
          ...prev,
          [imageType]: 'Processing image...'
        }));

        try {
          const reader = new FileReader();
          reader.onload = (e) => {
            const base64Image = e.target.result;
            const imageSizeMB = (base64Image.length * 0.75) / 1024 / 1024;
            
            // Accept up to 5MB for profile_image (company_logo remains 1MB)
            const maxSize = imageType === 'profile_image' ? 5 : 1;
            if (imageSizeMB > maxSize) {
              setErrors(prev => ({
                ...prev,
                [imageType]: `❌ Too large (${imageSizeMB.toFixed(2)}MB). Max ${maxSize}MB. Compress it!`
              }));
              return;
            }
       
            setFormData(prev => ({
              ...prev,
              [imageType]: base64Image 
            }));

            setErrors(prev => ({
              ...prev,
              [imageType]: `✓ Ready (${imageSizeMB.toFixed(2)}MB)`
            }));
          };
          
          reader.onerror = (readerError) => {
            console.error('FileReader error:', readerError);
            setErrors(prev => ({
              ...prev,
              [imageType]: 'Failed to process image. Please try again.'
            }));
          };
          
          reader.readAsDataURL(file);
          
        } catch (error) {
          console.error('Local upload error:', error);
          setErrors(prev => ({
            ...prev,
            [imageType]: 'Failed to process image. Please try again.'
          }));
        }
      };

      uploadImageLocally(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const validationErrors = validateDigitalCardForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      setLoading(false);
      return;
    }
    const completeCardData = {
      ...formData,
      contact_info: {
        phone: formData.contact_info?.phone || '',
        whatsapp: formData.contact_info?.whatsapp || '',
        email: formData.contact_info?.email || ''
      },
      social_media: {
        linkedin: formData.social_media?.linkedin || '',
        instagram: formData.social_media?.instagram || ''
      },
      description: `Hello there, I'm ${formData.name.split(' ')[0]}.
Please don't hesitate to save
and contact me.`,
      visibility: formData.visibility || {
        show_phone: true,
        show_whatsapp: true
      },
      ...(formData.profile_image && formData.profile_image.startsWith('data:') && { profile_image: formData.profile_image }),
      ...(formData.url_slug && { url_slug: formData.url_slug })
    };
    
    try {
      const result = await onSave(completeCardData);
    } catch (error) {
      console.error('❌ Error saving digital card:', error);
      setErrors({ submit: error.message || 'Failed to save card' });
      alert('Failed to save: ' + (error.message || 'Unknown error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="digital-id-form-container">
      <div className="form-header">
        <h2>{editingCard ? 'Edit Digital ID Card' : 'Create New Digital ID Card'}</h2>
        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => setShowPreview(!showPreview)}
            className="preview-button"
          >
            {showPreview ? 'Hide Preview' : 'Show Preview'}
          </button>
        </div>
      </div>

      <div className={`form-layout ${showPreview ? 'split-view' : 'single-view'}`}>
        <div className="form-section">
          <form onSubmit={handleSubmit} className="digital-id-form">
            {/* Basic Information */}
            <div className="form-group-section">
              <h3>Basic Information</h3>
              
              <div className="form-group">
                <label htmlFor="name">Full Name *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Enter full name"
                />
                {errors.name && <span className="error-text">{errors.name}</span>}
              </div>
              
              <div className="form-group">
                <label htmlFor="url_slug">Custom URL Slug (optional)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#666', fontSize: '14px' }}>meet.jethro-inc.id/card/</span>
                  <input
                    type="text"
                    id="url_slug"
                    name="url_slug"
                    value={formData.url_slug || ''}
                    onChange={handleChange}
                    className={errors.url_slug ? 'error' : ''}
                    placeholder={formData.name ? (() => {
                      const parts = formData.name.trim().split(/\s+/).filter(p => p);
                      if (parts.length === 1) return parts[0].toLowerCase();
                      return `${parts[0]}-${parts[parts.length - 1]}`.toLowerCase();
                    })() : '1st-lastname'}
                    style={{ flex: 1 }}
                  />
                </div>
                <small style={{ color: '#666', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Default: first + last name (e.g., "nerox-kira"). Leave blank for auto-generation.
                </small>
                {errors.url_slug && <span className="error-text">{errors.url_slug}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="position">Position/Title *</label>
                <input
                  type="text"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleChange}
                  className={errors.position ? 'error' : ''}
                  placeholder="e.g., DIRECTOR, MANAGER"
                />
                {errors.position && <span className="error-text">{errors.position}</span>}
              </div>
            </div>

            {/* Contact Information */}
            <div className="form-group-section">
              <h3>Contact Information</h3>
              
              <div className="form-group">
                <label htmlFor="contact_info.email">Email</label>
                <input
                  type="email"
                  id="contact_info.email"
                  name="contact_info.email"
                  value={formData.contact_info.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="email@company.com"
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="contact_info.phone">Office Phone (Jethro)</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    id="show_phone"
                    checked={formData.visibility?.show_phone !== false}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        visibility: {
                          ...prev.visibility,
                          show_phone: e.target.checked
                        }
                      }));
                    }}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  <label htmlFor="show_phone" style={{ margin: 0, fontSize: '14px', cursor: 'pointer' }}>
                    Show on Digital Card
                  </label>
                </div>
                <input
                  type="tel"
                  id="contact_info.phone"
                  name="contact_info.phone"
                  value={formData.contact_info.phone}
                  onChange={handleChange}
                  className={errors.phone ? 'error' : ''}
                  placeholder="+6221 59992864"
                />
                {errors.phone && <span className="error-text">{errors.phone}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="contact_info.whatsapp">WhatsApp Pribadi</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                  <input
                    type="checkbox"
                    id="show_whatsapp"
                    checked={formData.visibility?.show_whatsapp !== false}
                    onChange={(e) => {
                      setFormData(prev => ({
                        ...prev,
                        visibility: {
                          ...prev.visibility,
                          show_whatsapp: e.target.checked
                        }
                      }));
                    }}
                    style={{ width: 'auto', cursor: 'pointer' }}
                  />
                  <label htmlFor="show_whatsapp" style={{ margin: 0, fontSize: '14px', cursor: 'pointer' }}>
                    Show on Digital Card
                  </label>
                </div>
                <input
                  type="tel"
                  id="contact_info.whatsapp"
                  name="contact_info.whatsapp"
                  value={formData.contact_info.whatsapp}
                  onChange={handleChange}
                  className={errors.whatsapp ? 'error' : ''}
                  placeholder="+62 896 7883 1698"
                />
                {errors.whatsapp && <span className="error-text">{errors.whatsapp}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="address">Office Address</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Complete office address"
                />
              </div>
            </div>

            {/* Social Media */}
            <div className="form-group-section">
              <h3>Social Media</h3>
              
              <div className="form-group">
                <label htmlFor="social_media.linkedin">LinkedIn URL</label>
                <input
                  type="url"
                  id="social_media.linkedin"
                  name="social_media.linkedin"
                  value={formData.social_media.linkedin}
                  onChange={handleChange}
                  placeholder="https://linkedin.com/in/yourprofile"
                />
              </div>
            </div>

            {/* Images */}
            <div className="form-group-section">
              <h3>Images</h3>
              
              <div className="form-group">
                <label htmlFor="profile_image">Profile Image</label>
                <input
                  type="file"
                  id="profile_image"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'profile_image')}
                />
                {errors.profile_image && (
                  <div className={errors.profile_image.startsWith('Warning:') ? 'warning-message' : 'error-message'}>
                    {errors.profile_image}
                  </div>
                )}
                {formData.profile_image && (
                  <div className="image-preview">
                    <img src={formData.profile_image} alt="Profile Preview" />
                    <p className="image-info">✓ Image uploaded successfully</p>
                  </div>
                )}
                {!formData.profile_image && (
                  <div className="image-preview">
                    <img src="/assets/2559.jpg" alt="Default Preview" style={{opacity: 0.5}} />
                    <p className="image-info" style={{color: '#999'}}>Default image (will be used if not uploaded)</p>
                  </div>
                )}
              </div>
            </div>

            {/* Form Actions */}
            <div className="form-actions">
              <button type="button" onClick={onCancel} className="cancel-button">
                Cancel
              </button>
              <button 
                type="submit" 
                disabled={loading} 
                className="save-button"
              >
                {loading ? 'Saving...' : (editingCard ? 'Update Card' : 'Create Card')}
              </button>
            </div>
          </form>
        </div>

        {showPreview && (
          <div className="preview-section">
            <h3>Preview</h3>
            <div className="preview-container">
              <DigitalIDCard cardData={formData} isPreview={true} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DigitalIDForm;
